import React, { useCallback, useState } from 'react';
import api from '../api';
import Toast from './Toast';

function formatYearEndMessage(data) {
  if (!data || typeof data !== 'object') return 'Year end completed.';
  if (data.status === 'closed') {
    const profit = Number(data.net_profit || 0).toLocaleString();
    const je = data.journal_entry_id ? ` (journal #${data.journal_entry_id})` : '';
    return `Year ${data.year} closed. Net P&L moved to retained earnings: ${profit}${je}.`;
  }
  if (data.status === 'nothing_to_close') {
    const profit = Number(data.net_profit || 0).toLocaleString();
    return `Nothing to close for ${data.year}. Net profit is ${profit}.`;
  }
  if (data.status) {
    return `Year end status: ${data.status}.`;
  }
  return 'Year end completed.';
}

const MonthEnd = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [runDepreciation, setRunDepreciation] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  const clearToast = useCallback(() => setToast(null), []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type, key: Date.now() });
  };

  const runMonthEnd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post('/month-end/', {
        start_date: startDate,
        end_date: endDate,
        run_depreciation: runDepreciation,
      });
      const depCount = (res.data.depreciation_posted || []).length;
      showToast(
        `Month end ${res.data.status || 'done'}. Depreciation rows posted: ${depCount}.`,
        'success'
      );
    } catch (err) {
      showToast(err.response?.data?.error || 'Month end failed.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const runYearEnd = async () => {
    setBusy(true);
    try {
      const res = await api.post('/year-end/', { year: Number(year) });
      const type = res.data?.status === 'nothing_to_close' ? 'info' : 'success';
      showToast(formatYearEndMessage(res.data), type);
    } catch (err) {
      showToast(err.response?.data?.error || 'Year end failed.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {toast && (
        <Toast
          key={toast.key}
          message={toast.message}
          type={toast.type}
          onClose={clearToast}
          durationMs={5000}
        />
      )}

      <div className="flex-between">
        <div>
          <h2 className="page-title">Month / Year End</h2>
          <p className="page-subtitle">Close accounting periods and roll P&amp;L into retained earnings.</p>
        </div>
      </div>

      <div className="month-end-grid">
        <form onSubmit={runMonthEnd} className="page-card month-end-card">
          <h3 className="month-end-card-title">Close Month</h3>
          <p className="month-end-card-lead">
            Lock the selected date range and optionally post depreciation for fixed assets.
          </p>
          <div className="form-group">
            <label htmlFor="me-start">Start date</label>
            <input
              id="me-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="me-end">End date</label>
            <input
              id="me-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />
          </div>
          <label className="month-end-check">
            <input
              type="checkbox"
              checked={runDepreciation}
              onChange={(e) => setRunDepreciation(e.target.checked)}
            />
            <span>Also post monthly depreciation for all fixed assets</span>
          </label>
          <div className="month-end-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Processing…' : 'Close month end'}
            </button>
          </div>
        </form>

        <div className="page-card month-end-card">
          <h3 className="month-end-card-title">Close Year</h3>
          <p className="month-end-card-lead">Move P&amp;L balances into retained earnings for the selected year.</p>
          <div className="form-group">
            <label htmlFor="me-year">Year</label>
            <input
              id="me-year"
              type="number"
              min="2000"
              max="2100"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              required
            />
          </div>
          <div className="month-end-actions">
            <button type="button" className="btn btn-success" onClick={runYearEnd} disabled={busy}>
              {busy ? 'Processing…' : 'Run year end'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthEnd;
